import { useState, useEffect, useCallback } from 'react';
import { generateClient, GraphQLResult } from 'aws-amplify/api';
import { Site } from '../types';
import { LIST_SITES } from '../graphql/queries';

const client = generateClient();

interface ListSitesResponse {
  listSites: { items: Site[] };
}

interface UseSitesOptions {
  includeTrash?: boolean;
  teamId?: string | null;
}

export function useSites(options: UseSitesOptions = {}) {
  const { includeTrash = false, teamId } = options;
  const [sites, setSites] = useState<Site[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSites = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await client.graphql({
        query: LIST_SITES,
        variables: {
          includeTrash,
          teamId: teamId || undefined,
        },
      }) as GraphQLResult<ListSitesResponse>;

      setSites(response.data?.listSites?.items || []);
    } catch (err: unknown) {
      const gqlErr = err as { errors?: Array<{ message: string }> };
      const msg = gqlErr?.errors?.[0]?.message || String(err);
      console.error('Error fetching sites:', msg, err);
      setError('Failed to load sites. Please try again.');

      // For development without backend, use mock data
      if (import.meta.env.DEV) {
        setSites(getMockSites());
      }
    } finally {
      setIsLoading(false);
    }
  }, [includeTrash, teamId]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  return {
    sites,
    isLoading,
    error,
    refetch: fetchSites,
  };
}

// Mock data for development
function getMockSites(): Site[] {
  return [
    {
      siteId: 'site-1',
      userId: 'user-1',
      name: 'Example Website',
      url: 'https://example.com',
      description: 'Main corporate website',
      status: 'active',
      createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      lastScanId: 'scan-1',
      lastScanAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      lastScanScore: 78,
      lastScanGrade: 'C',
    },
    {
      siteId: 'site-2',
      userId: 'user-1',
      name: 'Company Blog',
      url: 'https://blog.example.com',
      status: 'active',
      createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      lastScanId: 'scan-2',
      lastScanAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      lastScanScore: 92,
      lastScanGrade: 'A',
    },
    {
      siteId: 'site-3',
      userId: 'user-1',
      name: 'E-commerce Store',
      url: 'https://shop.example.com',
      description: 'Online store with product catalog',
      status: 'active',
      createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ];
}
